import { useEffect, useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { SectionLabel } from '../../../components/ui/section-label';
import {
  useAdminUpdateUserProfile,
  type AdminUser,
} from '../../../api/extensions/useAdminOps';
import { notify } from '../../../utils/notifications';
import { cn } from '../../../lib/utils';
import { AdminUserProfileFormBody } from '../../../components/admin/AdminUserProfileFormBody';
import {
  buildAdminProfileDiff,
  emptyAdminProfileForm,
  userToAdminProfileForm,
  type AdminProfileFieldKey,
  type AdminProfileFormData,
} from '../../../components/admin/adminProfileForm.utils';

// Required-for-user fields are surfaced visually with `*` inside the shared
// form body. Admin saves are NOT blocked by missing required fields — the
// indicator is advisory, telling the admin which fields the user themselves
// must complete.

export default function ProfileTab({ user }: { user: AdminUser }) {
  const updateMutation = useAdminUpdateUserProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<AdminProfileFormData>(emptyAdminProfileForm());

  // Hydrate the draft from `user` whenever the prop changes — also resets the
  // draft if the admin opened the tab, started editing, then navigated to a
  // different user via deep link.
  useEffect(() => {
    setFormData(userToAdminProfileForm(user));
    setIsEditing(false);
  }, [user]);

  const handleFieldChange = (field: AdminProfileFieldKey, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const original = useMemo(() => userToAdminProfileForm(user), [user]);
  const hasChanges = useMemo(
    () =>
      (Object.keys(formData) as AdminProfileFieldKey[]).some(
        (k) => formData[k] !== original[k],
      ),
    [formData, original],
  );

  const handleSave = async () => {
    const diff = buildAdminProfileDiff(original, formData);
    // No-op save: nothing changed. Cleaner than firing an empty PUT.
    if (Object.keys(diff).length === 0) {
      setIsEditing(false);
      return;
    }
    try {
      await updateMutation.mutateAsync({ userId: user.id, data: diff });
      notify.success('Profile updated');
      setIsEditing(false);
    } catch (err: any) {
      notify.error(err?.message || 'Failed to update profile');
    }
  };

  const handleCancel = () => {
    setFormData(original);
    setIsEditing(false);
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-4 p-4 border-b flex-wrap">
        <div>
          <h2 className="font-semibold">Profile</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Fields marked <span className="text-destructive">*</span> are
            required when this user completes their own profile. Admins can
            save any single field independently.
          </p>
        </div>
        {!isEditing ? (
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit profile
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={updateMutation.isPending || !hasChanges}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </div>

      <div className="p-6 space-y-6">
        <AdminUserProfileFormBody
          formData={formData}
          isEditing={isEditing}
          onFieldChange={handleFieldChange}
        />

        {user.created_at && (
          <AccountSection>
            <Label className="text-sm">Created</Label>
            <ReadOnlyValue
              value={new Date(user.created_at).toLocaleDateString()}
            />
          </AccountSection>
        )}
      </div>
    </div>
  );
}

function AccountSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <SectionLabel color="muted">Account</SectionLabel>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>{children}</div>
      </div>
    </div>
  );
}

function ReadOnlyValue({ value }: { value: string | null | undefined }) {
  return (
    <div
      className={cn(
        'flex h-10 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm opacity-70',
      )}
    >
      {value ? (
        <span className="truncate">{value}</span>
      ) : (
        <span className="text-muted-foreground/60">—</span>
      )}
    </div>
  );
}
